import { PrefetchPageLinks, useFetcher, useNavigate, useParams, useRouteLoaderData } from '@remix-run/react'
import { json, type ActionFunctionArgs } from '@remix-run/server-runtime'
import * as React from 'react'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { bookmarkDocument, unbookmarkDocument } from '#app/services/document.ts'
import { requireUserId } from '#app/utils/auth.server.ts'
import { removeStorageItem } from '#app/utils/local-storage.ts'
import { type loader as documentLoader } from './$slug._route.tsx'
import { RenderDocument } from './components/render-document.tsx'
import { VariationsSelect } from './components/variation-select.tsx'
import { convertDocumentFormToQueryParams } from './utils.ts'

export async function action({ request, params }: ActionFunctionArgs) {
	let userId = await requireUserId(request)
	let formData = await request.formData()

	if (!params.slug) {
		return new Response(null)
	}

	if (formData.get('action') === 'bookmark') {
		await bookmarkDocument(userId, params.slug)
		return json({ success: true })
	} else if (formData.get('action') === 'un-bookmark') {
		await unbookmarkDocument(userId, params.slug)
		return json({ success: true })
	}

	return new Response(null)
}

export default function CartulacionDocument() {
	let {
		document: doc,
		placeholders,
		isBookmarked,
		variations,
	} = useRouteLoaderData<typeof documentLoader>('routes/_app+/plantillas+/$category+/$slug._route')!
	const params = useParams()

	const navigate = useNavigate()

	return (
		<div>
			<div className="sticky top-0 flex flex-col gap-2 bg-background pb-3 pt-8 md:flex-row md:justify-between">
				<div className="flex flex-row flex-wrap gap-2">
					<Button
						variant="outline"
						onClick={() => {
							submitForm()
							window.open(`/resources/download-doc/${doc!.slug}?${convertDocumentFormToQueryParams()}`, '_blank')
							// removeAutoSaveData(params.slug ?? '')
						}}
					>
						<Icon name="docx">Descargar en Word</Icon>
					</Button>
					<Button
						variant="outline"
						onClick={() => {
							submitForm()
							window.open(`/resources/download-pdf/${doc!.slug}?${convertDocumentFormToQueryParams()}`, '_blank')
							removeAutoSaveData(params.slug ?? '')
						}}
					>
						<Icon name="pdf">Descargar en PDF</Icon>
					</Button>
					<BookmarkButton isBookmarked={isBookmarked} />
					<Button
						onClick={() => {
							navigate(`/plantillas/${params.category}/${params.slug}/variation`)
						}}
						variant="outline"
					>
						<Icon name="plus-rectangle">Crear otra version</Icon>
					</Button>
				</div>
				<VariationsSelect variations={variations} />
			</div>
			<Spacer size="4xs" />
			<RenderDocument content={doc.content} placeholders={placeholders} />
			<PrefetchPageLinks page={`/${params.category}/${params.slug}/variation`} />
		</div>
	)
}

export function BookmarkButton({ isBookmarked, disabled }: { isBookmarked: boolean; disabled?: boolean }) {
	const [isBookmarkedLocal, setIsBookmarkedLocally] = React.useState(isBookmarked)

	const fetcher = useFetcher()

	return (
		<fetcher.Form method="post">
			<input type="hidden" name="action" value={isBookmarkedLocal ? 'un-bookmark' : 'bookmark'} />
			<Button
				variant="outline"
				type="submit"
				disabled={disabled}
				onClick={() => setIsBookmarkedLocally(!isBookmarkedLocal)}
			>
				<Icon name={isBookmarkedLocal ? 'bookmark-filled' : 'bookmark'}>
					{isBookmarkedLocal ? 'Quitar de marcadores' : 'Anadir a marcadores'}
				</Icon>
			</Button>
		</fetcher.Form>
	)
}

export function submitForm() {
	let form: HTMLFormElement | null = document.querySelector('#document-form')
	if (!form) {
		return
	}
	form.submit()
}

export function removeAutoSaveData(slug: string) {
	removeStorageItem(['document-form-data', slug])
}
